import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../api';
import toast from 'react-hot-toast';
import { ArrowRight, Mail, RefreshCw } from 'lucide-react';

const inputStyle = {
    padding: '14px 16px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-primary)',
    fontSize: '1rem',
    outline: 'none',
    width: '100%',
    transition: 'border-color var(--transition-fast)',
                  color: 'var(--color-text-primary)'
};

function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authService.verifyEmail(email, otp);
            toast.success('Email verified! You can now log in.');
            navigate('/auth/login');
        } catch (err) {
            toast.error(
                err.response?.data?.message
                || err.response?.data?.error
                || 'Verification failed. Please check your code.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) return toast.error('Please enter your email');
        setResending(true);
        try {
            await authService.resendOTP(email);
            toast.success('Verification code resent! Check your inbox (or server logs in local dev).');
        } catch (err) {
            toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to resend code.');
        } finally {
            setResending(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-primary)', padding: '2rem',
                  color: 'var(--color-text-primary)' }}>
            <div style={{ width: '100%', maxWidth: '480px' }}>
                <Link to="/" className="flex items-center gap-xs" style={{ marginBottom: '2.5rem', textDecoration: 'none', justifyContent: 'center' }}>
                    <div style={{
                        background: 'var(--gradient-brand)',
                        color: 'white', width: '36px', height: '36px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', borderRadius: '10px',
                        fontWeight: 800, fontSize: '1.4rem',
                    }}>L</div>
                    <span style={{ fontWeight: 800, fontSize: '1.6rem', color: 'var(--color-text-primary)', letterSpacing: '-0.5px' }}>Learnly</span>
                </Link>

                <div style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: '2.5rem', border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)' }}>
                    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            background: 'rgba(47,124,123,0.1)', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', margin: '0 auto 1rem'
                        }}>
                            <Mail size={30} color="var(--color-primary)" />
                        </div>
                        <h1 style={{ fontSize: '1.85rem', marginBottom: '0.5rem' }}>Verify Your Email</h1>
                        <p className="text-muted" style={{ fontSize: '0.95rem' }}>
                            We've sent a verification code to your email. Please check your inbox.
                        </p>
                    </div>

                    {email && (
                        <div style={{
                            background: 'rgba(47,124,123,0.07)', border: '1px solid rgba(47,124,123,0.2)',
                            borderRadius: 'var(--radius-md)', padding: '0.85rem 1rem', marginBottom: '1.75rem',
                            fontSize: '0.9rem', color: 'var(--color-text-secondary)', textAlign: 'center'
                        }}>
                            Code sent to: <strong>{email}</strong>
                        </div>
                    )}

                    <form className="flex flex-col gap-lg" onSubmit={handleSubmit}>
                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Email Address</label>
                            <input
                                type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                readOnly={!!searchParams.get('email')}
                                style={{ ...inputStyle, backgroundColor: searchParams.get('email') ? '#f8fafc' : 'var(--color-bg-primary)' }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Verification Code</label>
                            <input
                                type="text" required value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder="Enter 6-digit code"
                                maxLength={6}
                                style={{ ...inputStyle, letterSpacing: '0.3rem', fontSize: '1.4rem', textAlign: 'center' }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                            />
                        </div>

                        <button
                            disabled={loading}
                            className="btn btn-primary shadow-md flex items-center justify-center gap-sm w-full"
                            style={{ padding: '16px', fontSize: '1.1rem' }}
                        >
                            {loading ? 'Verifying...' : <> Verify Email <ArrowRight size={20} /></>}
                        </button>
                    </form>

                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: '1.5rem', paddingTop: '1.5rem', textAlign: 'center' }}>
                        <p className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>Didn't receive the code?</p>
                        <button
                            onClick={handleResend} disabled={resending}
                            className="btn btn-outline"
                            style={{ padding: '0.6rem 1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            {resending ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={16} />}
                            Resend Code
                        </button>
                        <p style={{ marginTop: '1.25rem', fontSize: '0.9rem' }}>
                            <Link to="/auth/login" style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>← Back to Login</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VerifyEmail;
